import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Activity, Clock, LogOut, Crown, LogIn, ShieldCheck, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { nextBriefingDate, isMarketClosedToday } from "@/lib/idxCalendar";

const NAV = [
  { id: "analisis", label: "Analisis AI" },
  { id: "compare", label: "Bandingkan" },
  { id: "screener", label: "Screener" },
  { id: "robot", label: "Robot AI" },
  { id: "briefing", label: "Briefing" },
  { id: "scalping", label: "Scalping" },
  { id: "watchlist", label: "Watchlist" },
  { id: "calculator", label: "Kalkulator" },
  { id: "probability", label: "Probabilitas" },
  { id: "calendar", label: "Kalender" },
  { id: "news", label: "Berita" },
  { id: "telegram", label: "Telegram" },
  { id: "membership", label: "Membership" },
];

const two = (n) => String(n).padStart(2, "0");

export default function Header() {
  const { user, login, logout } = useAuth();
  const [now, setNow] = useState(new Date());
  const [isAdmin, setIsAdmin] = useState(false);
  const [theme, setTheme] = useState(() => (typeof document !== "undefined" ? document.documentElement.getAttribute("data-theme") : "dark") || "dark");

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    document.documentElement.setAttribute("data-theme", next);
    localStorage.setItem("fas_theme", next);
  };

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    api.get("/admin/check").then((r) => setIsAdmin(!!r.data.is_admin)).catch(() => setIsAdmin(false));
  }, [user]);

  const wib = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Jakarta" }));
  const clock = `${two(wib.getHours())}:${two(wib.getMinutes())}:${two(wib.getSeconds())}`;

  const next = nextBriefingDate(wib);
  const diff = Math.max(0, next - wib);
  const ch = two(Math.floor(diff / 3600000));
  const cm = two(Math.floor((diff % 3600000) / 60000));
  const cs = two(Math.floor((diff % 60000) / 1000));
  const closedToday = isMarketClosedToday(wib);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    const vid = localStorage.getItem("fas_visitor_id") || "anon";
    api.post("/track/visit", { visitor_id: vid, path: id, type: "feature" }).catch(() => {});
  };

  return (
    <header className="sticky top-0 z-50 glass-header border-b border-[var(--border)]" data-testid="main-header">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 shrink-0 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <div className="w-9 h-9 rounded-lg bg-[var(--accent)] flex items-center justify-center pulse-glow">
            <Activity size={20} className="text-[#07090E]" strokeWidth={2.5} />
          </div>
          <div className="leading-none">
            <span className="font-heading font-extrabold text-lg tracking-tight">FAS</span>
            <span className="font-heading font-extrabold text-lg tracking-tight text-accent">Trading</span>
          </div>
        </div>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.filter((n) => n.id !== "telegram" || isAdmin).map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => scrollTo(n.id)}
              className="px-3 py-2 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors rounded-md hover:bg-[var(--surface-hover)]"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <div className="hidden md:flex items-center gap-3 px-3 py-1.5 rounded-lg bg-[var(--surface-base)] border border-[var(--border)]">
            <div className="flex items-center gap-1.5">
              <Clock size={14} className="text-accent" />
              <span className="font-mono-data text-sm font-semibold" data-testid="wib-clock">{clock}</span>
              <span className="text-[10px] text-[var(--text-muted)] font-mono-data">WIB</span>
            </div>
            <div className="w-px h-4 bg-[var(--border)]" />
            <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{closedToday ? "Bursa Tutup" : "Briefing"}</span>
            <span className={`font-mono-data text-sm font-semibold ${closedToday ? "text-[var(--text-secondary)]" : "text-amber"}`} data-testid="briefing-countdown">{ch}:{cm}:{cs}</span>
          </div>

          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            title={theme === "light" ? "Mode Gelap" : "Mode Terang"}
            aria-label="Ganti tema"
            className="w-9 h-9 rounded-lg flex items-center justify-center bg-[var(--surface-base)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-accent hover:border-[var(--accent)]/40 transition-colors"
          >
            {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
          </button>

          {user ? (
            <div className="flex items-center gap-2">
              {(() => {
                const isTrial = user.membership_active && user.membership_plan === "trial";
                const isAdminPlan = user.membership_plan === "admin";
                let daysLeft = 0;
                if (user.membership_active && user.membership_expires) {
                  daysLeft = Math.max(1, Math.ceil((new Date(user.membership_expires) - Date.now()) / 86400000));
                }
                const label = !user.membership_active
                  ? "Free"
                  : isAdminPlan
                  ? "ADMIN"
                  : isTrial
                  ? `TRIAL · ${daysLeft}h`
                  : daysLeft
                  ? `VIP · ${daysLeft}h`
                  : "VIP";
                return (
                  <div className={`hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${user.membership_active ? "bg-[var(--gold)]/15 text-gold border border-[var(--gold)]/30" : "bg-[var(--surface-hover)] text-[var(--text-secondary)] border border-[var(--border)]"}`} data-testid="user-tier-badge">
                    <Crown size={12} />
                    {label}
                  </div>
                );
              })()}
              {isAdmin && (
                <a href="/admin" data-testid="admin-link" className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[var(--accent)]/15 text-accent border border-[var(--accent)]/30 hover:bg-[var(--accent)]/25 transition-colors">
                  <ShieldCheck size={12} /> Admin
                </a>
              )}
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-[var(--border)]" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[var(--accent)] text-[#07090E] flex items-center justify-center font-bold text-sm">
                  {(user.name || "U")[0]}
                </div>
              )}
              <button onClick={logout} data-testid="logout-btn" className="p-2 rounded-md hover:bg-[var(--surface-hover)] text-[var(--text-secondary)] hover:text-bear transition-colors">
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <Button onClick={login} data-testid="login-btn" className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-[#07090E] font-semibold h-9 rounded-lg">
              <LogIn size={16} className="mr-1.5" /> Masuk
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
